import { regionEn } from "@/lib/seo-keywords";
// /en/notary/$act/in/$location — English Notary act × Location combo
// 8 top acts × 30 expat-priority locations = 240 SEO pages for international clients.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NOTARY_ACTS, getNotaryAct } from "@/data/notary-acts";
import { getLocation, locations } from "@/data/locations";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { RelatedServicesInLocation } from "@/components/RelatedServicesInLocation";
import { EntityPanel } from "@/components/EntityPanel";
import NotaryActDeepContent from "@/components/NotaryActDeepContent";
import {
  ScrollText, MapPin, FileCheck2, ShieldCheck, Clock,
  MessageCircle, Phone, BadgeCheck, Globe2, ArrowRight, Building2, Landmark,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// Mirrors TH TOP_NOTARY_ACT_SLUGS — keep in sync.
export const EN_TOP_NOTARY_ACT_SLUGS = [
  "affidavit",
  "power-of-attorney",
  "certified-true-copy",
  "jurat",
  "affidavit-single",
  "travel-consent",
  "certify-translation",
  "apostille-request",
  // Phase NOTARY-EXPAND++ — sync with TH TOP list
  "certify-signature",
  "statutory-declaration",
  "specific-poa",
  "copy-passport",
  "copy-education",
  "oath",
  "affidavit-support",
  "affidavit-name",
  // Phase 7C — Notary depth expansion (14 new acts → 30 total, EN mirror)
  "copy-id",
  "copy-house-registration",
  "copy-birth-cert",
  "copy-marriage-cert",
  "dbd-signature",
  "certify-signature-juristic",
  "affidavit-single-status",
  "certify-photo",
  "life-certificate",
  "proof-of-address",
  "certify-no-criminal",
  "certify-signing-authority",
  "corporate-resolution",
  "contract-property-foreigner",
] as const;

// 30 highest-intent locations for international clients (expat hubs + key BKK districts + key provinces).
export const EN_TOP_LOCATIONS = [
  // Expat hubs
  "hub-wireless-road", "hub-sathorn-cbd", "hub-silom", "hub-asoke-corridor",
  "hub-sukhumvit-24", "hub-sukhumvit-39", "hub-sukhumvit-49", "hub-langsuan",
  "hub-rama-9-ratchada", "hub-nichada-pakkret", "hub-pattaya", "hub-hua-hin",
  "hub-phuket-patong", "hub-chiangmai-nimman", "hub-koh-samui",
  // BKK key districts
  "bkk-watthana", "bkk-khlong-toei", "bkk-bang-rak", "bkk-pathum-wan",
  "bkk-sathon", "bkk-huai-khwang", "bkk-chatuchak", "bkk-phra-khanong",
  "bkk-ratchathewi",
  // Key provinces
  "province-chiang-mai", "province-phuket", "province-chon-buri",
  "province-prachuap-khiri-khan", "province-surat-thani", "province-rayong",
] as const;

function isTop(slug: string): boolean {
  return (EN_TOP_NOTARY_ACT_SLUGS as readonly string[]).includes(slug);
}
function isTopLoc(slug: string): boolean {
  return (EN_TOP_LOCATIONS as readonly string[]).includes(slug);
}

export const Route = createFileRoute("/en/notary/$act/in/$location")({
  loader: ({ params }) => {
    if (!isTop(params.act)) throw notFound();
    if (!isTopLoc(params.location)) throw notFound();
    if (!getNotaryAct(params.act)) throw notFound();
    if (!getLocation(params.location)) throw notFound();
    return { actSlug: params.act, locSlug: params.location };
  },
  head: ({ params }) => {
    const n = getNotaryAct(params.act);
    const loc = getLocation(params.location);
    if (!n || !loc) return { meta: [{ title: ctaTitle("Page not found", "/en/notary/$act/in/$location", "en")}] };
    const url = `${SITE}/en/notary/${n.slug}/in/${loc.slug}`;
    const title = `${n.nameEn} in ${loc.nameEn} — Notary Public Thailand | From ฿${n.priceFromTh.toLocaleString()}`;
    const desc = `${n.nameEn} for clients in ${loc.nameEn}, Thailand. Court-licensed Notarial Services Attorney. Same-day available · Embassy-accepted · Apostille/MFA add-on. Used by 2,000+ expats and Fortune-500 HR teams. Message us for a same-day answer.`;
    const faqs = [
      { q: `How much does ${n.nameEn} cost in ${loc.nameEn}?`,
        a: `${n.nameEn} starts at ฿${n.priceFromTh.toLocaleString()} and is completed within ${n.turnaround} by a Thai Notarial Services Attorney (Lawyers Council of Thailand). For clients in ${loc.nameEn} we add door-to-door pickup/delivery and can chain Apostille or embassy legalisation.` },
      { q: `Where can a ${n.nameEn} be used internationally?`,
        a: `Notarial acts issued in Thailand are accepted worldwide once legalised. Hague-member countries (124 nations) accept the MFA Apostille (effective 13 Dec 2023). Non-Hague countries require destination-embassy legalisation. NYC handles both end-to-end so you do not visit multiple agencies.` },
      { q: `How do clients in ${loc.nameEn} submit documents?`,
        a: `Three options: (1) scan/photo via LINE @nycli or email and receive a quote within 15-30 minutes, (2) walk-in to our Bangkok Head Office (Lat Phrao 95) or branches in Khon Kaen / Udon Thani / Nong Khai, (3) request an attorney visit to ${loc.nameEn} (+฿1,500-5,000 depending on distance). Original documents must be signed in front of the notary.` },
      { q: `What documents are required for ${n.nameEn}?`,
        a: `${n.required.join(" · ")}. Clients in ${loc.nameEn} can send scans first — our team pre-checks for completeness, signature consistency, and translation quality before booking the in-person signing.` },
    ];
    return {
      meta: [
        { title: ctaTitle(title, "/en/notary/$act/in/$location", "en") }, { name: "description", content: ctaDesc(desc, "/en/notary/$act/in/$location", "en")},
        { name: "keywords", content: [
          `${n.nameEn} ${loc.nameEn}`,
          `notary public ${loc.nameEn}`,
          `Thai notary ${loc.nameEn}`,
          `${n.nameEn} Bangkok Thailand`,
          `${n.nameEn} cost`,
          `${n.nameEn} near me`,
        ].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/notary/$act/in/$location", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/notary/$act/in/$location", "en")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(title, "/en/notary/$act/in/$location", "en")},
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/en/notary/${n.slug}/in/${loc.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${n.nameEn} — ${loc.nameEn}`,
              alternateName: `${n.nameTh} · ${loc.nameTh}`,
              serviceType: "Notary Public",
              inLanguage: "en",
              areaServed: {
                "@type": "Place",
                name: loc.nameEn,
                ...(loc.lat && loc.lng ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } } : {}),
              },
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
                address: { "@type": "PostalAddress", addressCountry: "TH", addressLocality: loc.nameEn },
              },
              offers: { "@type": "Offer", price: n.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(`en-notary/${n.slug}/${loc.slug}`),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage", "@id": `${url}#faq`, inLanguage: "en",
              mainEntity: faqs.map((f) => ({
                "@type": "Question", name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo", "@id": `${url}#howto`, inLanguage: "en",
              name: `How to obtain ${n.nameEn} in ${loc.nameEn}, Thailand`,
              totalTime: "PT1D",
              estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: String(n.priceFromTh) },
              step: [
                { "@type": "HowToStep", position: 1, name: "Submit request", text: `Send document scans + request via LINE @nycli or email. Team in ${loc.nameEn} responds within 15-30 minutes.` },
                { "@type": "HowToStep", position: 2, name: "Confirm price & book", text: "Approve quote and schedule a signing slot at the office or request an attorney visit." },
                { "@type": "HowToStep", position: 3, name: "Sign before the notary", text: "Sign in front of a court-licensed Notarial Services Attorney with ID/passport verification." },
                { "@type": "HowToStep", position: 4, name: "Notarial seal", text: "Attorney signs, seals, and issues a bilingual Notarial Certificate." },
                { "@type": "HowToStep", position: 5, name: "Apostille / embassy (optional)", text: "Chain MFA Apostille or destination-embassy legalisation in 1-5 working days." },
                { "@type": "HowToStep", position: 6, name: "Delivery", text: `Pickup at office or courier (EMS/Grab/Lalamove) to ${loc.nameEn}.` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Notary Public", item: `${SITE}/en/notary` },
                { "@type": "ListItem", position: 3, name: n.nameEn, item: `${SITE}/en/notary/${n.slug}` },
                { "@type": "ListItem", position: 4, name: loc.nameEn, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: EnNotaryActLocationPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <Link to="/en/notary" className="mt-4 inline-block text-gold underline">Back to Notary hub</Link>
    </main>
  ),
});

function EnNotaryActLocationPage() {
  const { actSlug, locSlug } = Route.useLoaderData() as { actSlug: string; locSlug: string };
  const n = getNotaryAct(actSlug)!;
  const loc = getLocation(locSlug)!;

  const related = NOTARY_ACTS.filter((x) => x.slug !== n.slug && x.category === n.category).slice(0, 6);
  const otherTopActs = NOTARY_ACTS
    .filter((x) => isTop(x.slug) && x.slug !== n.slug)
    .slice(0, 6);
  const nearby = locations
    .filter((l) => l.slug !== loc.slug && isTopLoc(l.slug) && l.region === loc.region)
    .slice(0, 8);

  const faqs = [
    { q: `How much does ${n.nameEn} cost in ${loc.nameEn}?`,
      a: `Pricing starts at ฿${n.priceFromTh.toLocaleString()} with ${n.turnaround} turnaround. The fee covers attorney verification, notarial seal, and a bilingual (Thai/English) Notarial Certificate accepted by embassies and the Thai Ministry of Foreign Affairs. Door-to-door pickup/delivery within ${loc.nameEn} starts at ฿80 (Bangkok zones) or EMS for the rest of Thailand.` },
    { q: `Which countries accept a Thai ${n.nameEn}?`,
      a: `Worldwide, once legalised. Since 13 December 2023 Thailand has been a full party to the Hague Apostille Convention — meaning 124 member countries (USA, UK, Australia, all EU, Japan, Singapore, India, Brazil, etc.) accept the MFA Apostille without further embassy steps. Non-Hague destinations (UAE, Saudi Arabia, Vietnam, Taiwan, Canada, China) still need destination-embassy legalisation, which NYC chains for you.` },
    { q: `Can I sign a ${n.nameEn} online or by video?`,
      a: `No. Thai law (Lawyers Council Regulation on Notarial Services Attorneys B.E. 2551) requires the signer to appear in person before the attorney. We offer two alternatives that still satisfy this rule: (1) an in-office signing at our Bangkok Head Office or branches, (2) an attorney visit to your location in ${loc.nameEn} (+฿1,500-5,000 depending on distance and travel time).` },
    { q: `How do international clients in ${loc.nameEn} submit documents?`,
      a: `Three channels: (1) LINE @nycli or email — scans/photos receive a quote within 15-30 minutes; (2) walk-in at our Bangkok HQ (Lat Phrao 95), Khon Kaen, Udon Thani, or Nong Khai branches; (3) attorney visit to your home, hotel, condo, or corporate office in ${loc.nameEn}. Originals must be signed in person.` },
    { q: `What do I need to bring for ${n.nameEn}?`,
      a: `${n.required.join(" · ")}. All originals + valid passport (or Thai ID for Thai nationals). Translated supporting documents should be reviewed beforehand to avoid the most common embassy rejection cause: name/date inconsistency between Thai and English.` },
    { q: `How long does ${n.nameEn} take?`,
      a: `Standard: ${n.turnaround}. Same-day available for orders confirmed before 11:00 (+฿1,000 rush fee). Add 1-3 working days if you also need MFA Apostille; add 2-5 days for destination-embassy legalisation depending on country.` },
    { q: `Do you serve foreign clients in ${loc.nameEn}?`,
      a: `Yes — we serve over 2,000 expats and 200+ multinational companies annually. Service is delivered in English (and Japanese / Mandarin on request). Common client profiles in ${loc.nameEn} include corporate executives filing POAs, retirees obtaining proof-of-life or Affidavit of Single Status, families processing K-1 / spouse visas, and students notarising transcripts for WES / overseas admission.` },
    { q: `Is the document accepted by my country's embassy?`,
      a: `Yes — NYC has a 98.6% first-pass acceptance rate over the last 24 months across 60+ embassies in Bangkok. The Notarial Certificate we issue carries the attorney's seal, registration number, and bilingual wording matching the format the Ministry of Foreign Affairs requires for further Apostille / legalisation.` },
    { q: `Can I get a VAT invoice for company expense?`,
      a: `Yes — full VAT invoice (7%) under NYC Legal & Notary Service Co., Ltd. (DBD 0435567000061) for corporate accounting and tax deduction.` },
    { q: `Is your attorney officially licensed?`,
      a: `Yes — every signing attorney is a Notarial Services Attorney (ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร) registered with the Lawyers Council of Thailand under the patronage of His Majesty the King. Registration numbers are printed on every Notarial Certificate and can be verified at lawyerscouncil.or.th.` },
  ];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 flex flex-wrap gap-x-1 text-[12px] text-white/80">
            <Link to="/en" className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 hover:text-gold rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">Home</Link>
            <span className="px-1 py-1.5 text-white/30">/</span>
            <Link to="/en/notary" className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 hover:text-gold rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">Notary</Link>
            <span className="px-1 py-1.5 text-white/30">/</span>
            <Link to="/en/notary/$act" params={{ act: n.slug }} className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 hover:text-gold rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">{n.nameEn}</Link>
            <span className="px-1 py-1.5 text-white/30">/</span>
            <span className="min-h-[36px] inline-flex items-center px-1.5 py-1.5 text-white/85">{loc.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ScrollText className="h-3.5 w-3.5" /> Notary Public · {n.nameEn} · {loc.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            {n.nameEn} in {loc.nameEn}
          </h1>
          <p className="mt-2 text-base text-white/80">{n.nameTh} · {loc.nameTh}</p>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Court-licensed Thai Notarial Services Attorney serving international clients in {loc.nameEn}. Bilingual Notarial Certificate from ฿{n.priceFromTh.toLocaleString()}, ready in {n.turnaround}, accepted by embassies worldwide and acceptable for MFA Apostille under the Hague Convention (in force in Thailand since 13 December 2023).
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "From", v: `฿${n.priceFromTh.toLocaleString()}` },
              { k: "Turnaround", v: n.turnaround },
              { k: "Area", v: loc.nameEn },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300">
              <MessageCircle className="h-4 w-4" /> Chat on LINE — reply in 15 min
            </a>
            <a href="tel:+66832494999" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          {n.nameEn} for clients in {loc.nameEn} costs from ฿{n.priceFromTh.toLocaleString()} and is completed within {n.turnaround} by a court-licensed Thai Notarial Services Attorney. Documents are accepted worldwide via MFA Apostille (Hague) or destination-embassy legalisation.
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <MapPin className="inline h-6 w-6 text-amber-600" /> {n.nameEn} for clients in {loc.nameEn}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            {loc.nameEn} is one of the highest-demand zones for NYC's notary practice. Whether you are a corporate executive at a multinational, a long-term resident on a retirement or LTR visa, a digital nomad relocating between Bangkok and Chiang Mai, or a family processing a cross-border marriage or inheritance — the same legal framework applies: a Thai Notarial Services Attorney must verify your identity in person, witness the signing, affix the official seal, and issue a Notarial Certificate. NYC has handled this exact workflow for over 12,000 international clients since 2009.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            For {loc.nameEn} specifically we offer three delivery models. The fastest is an in-office signing at our Bangkok Head Office on Lat Phrao 95 — most clients can finish in 30-45 minutes including QC. The most popular for senior executives and retirees is an attorney visit to your condo, hotel, or office in {loc.nameEn} — we travel with the seal, a notary register, and pre-printed bilingual certificate templates, so the entire engagement is completed at your location in under an hour. The third model is a remote/courier model in which scans are pre-checked, original documents arrive by EMS, and only the in-person signing slot requires your presence.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            Documents we notarise here are accepted by US Citizenship and Immigration Services (USCIS), the UK Home Office, Australian Department of Home Affairs, German consulates, Singapore ACRA, the Hong Kong Companies Registry, BVI / Cayman corporate registries, and every Hague-member civil registry. Our 98.6% first-pass acceptance rate is the reason embassies, Big-4 audit firms, and major Bangkok law firms refer clients to us by name.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Common use cases — {n.nameEn}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.useCases.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-slate-700 sm:text-base">
            For clients in {loc.nameEn} the most frequent use cases cluster into four buckets. (1) Corporate filings — POAs, Resolutions, Certificates of Incumbency for offshore registries (BVI, Cayman, Singapore, Hong Kong) and cross-border M&A; (2) Visa &amp; immigration — Affidavit of Single Status for marriage abroad, Affidavit of Support (I-864), Police Clearance certification, FBI background check fingerprinting; (3) Education &amp; professional credentials — notarised transcripts, diplomas, and professional licences for WES, ECE, ICA, ECFMG, or licensing boards; (4) Family &amp; estate — Minor Travel Consent, Guardianship Consent, Affidavit of Heirship, Declaration of Trust, and Deed of Gift for cross-border estate planning.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">What to prepare</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {n.required.map((r) => (
              <li key={r} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {r}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-slate-700 sm:text-base">
            Clients in {loc.nameEn} can send scans through LINE @nycli for a free pre-check. Our team verifies completeness, signature consistency, name spelling between Thai and English documents, validity of certified copies (most authorities require copies less than 6 months old), and translation accuracy. The pre-check catches roughly 80% of issues that would otherwise cause embassy rejection — meaning your in-person signing is a one-shot process.
          </p>
        </section>

        <section className="mt-12 rounded-3xl border border-slate-200 bg-slate-50/50 p-6 sm:p-8">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Step-by-step process — {loc.nameEn}</h2>
          <ol className="mt-5 space-y-4">
            {[
              { day: "Day 0", title: "Submit request", text: `Send document scans + a brief description via LINE @nycli or email. Our ${loc.nameEn} coordinator confirms scope, fees, and timeline within 15-30 minutes during business hours.` },
              { day: "Day 0", title: "Pre-check & quote", text: "We pre-check the documents for completeness and embassy-format compliance, issue a fixed-fee quote, and propose a signing slot." },
              { day: "Day 1", title: "In-person signing", text: `Choose: walk-in to our Bangkok HQ, or attorney visit to your location in ${loc.nameEn}. Bring originals + valid passport (or Thai ID).` },
              { day: "Day 1", title: "Notarial seal", text: "Attorney verifies identity, witnesses the signature, applies the official notarial seal, and issues a bilingual Notarial Certificate registered in our notary log." },
              { day: "Day 1-4", title: "Apostille / Embassy (optional)", text: "If destination is Hague: MFA Apostille in 1-3 working days. If non-Hague: destination-embassy legalisation in 2-5 working days. NYC handles submission, payment, and pickup." },
              { day: "Day 1-5", title: "Delivery", text: `Pickup at office, courier to ${loc.nameEn} (Grab/Lalamove same-day in Bangkok zones, EMS for the rest), or direct dispatch to your destination authority abroad.` },
            ].map((t, i) => (
              <li key={i} className="flex gap-4 rounded-2xl border border-white bg-white p-4 shadow-sm">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/20 font-display text-sm font-extrabold text-amber-700">{i + 1}</div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{t.day}</div>
                  <h3 className="font-display text-base font-extrabold text-navy-deep">{t.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-700">{t.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Why NYC — {loc.nameEn}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              "Court-licensed Notarial Services Attorney (Lawyers Council of Thailand registration verifiable on every certificate)",
              "98.6% first-pass acceptance across 60+ embassies in Bangkok (24-month rolling)",
              "Bilingual certificate template — Thai/English — pre-aligned to MFA Apostille format",
              "End-to-end chain: Notary → MFA Apostille → destination embassy → courier to overseas counterparty",
              "Attorney visits across Bangkok, Pattaya, Hua Hin, Phuket, Chiang Mai, Koh Samui",
              "Full VAT invoice (7%) under NYC Legal & Notary Service Co., Ltd. — corporate-deductible",
              "Service delivered in English (default), with Japanese / Mandarin on request",
              "Used by Fortune-500 HR, Magic-Circle law firms, and Bangkok's leading audit/tax practices",
            ].map((w, i) => (
              <li key={i} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-800">
                <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {w}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Pricing · {loc.nameEn}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {n.nameEn} from ฿{n.priceFromTh.toLocaleString()}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-700 sm:text-base">
            The base fee covers attorney verification, notarial seal, bilingual certificate, log registration, and one round of corrections within 14 days. Add-ons (Apostille, embassy legalisation, attorney visit, courier, rush) are quoted transparently — no hidden charges. For corporate accounts and recurring filings we offer monthly retainers starting at ฿15,000/month covering 10 notarial acts plus priority booking.
          </p>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Standard turnaround: {n.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Same-day rush: +฿1,000 (orders before 11:00)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Attorney visit: +฿1,500 (Bangkok) / +฿2,500-5,000 (provinces)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> + MFA Apostille: ฿2,500 (includes government fee)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> + Embassy legalisation: ฿3,000-8,000 (varies by country)</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> VAT invoice (7%) — corporate-deductible</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300">
              <MessageCircle className="h-4 w-4" /> Get an English quote on LINE
            </a>
            <a href="mailto:contact@nyclegal.co.th" className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-navy-deep/20 bg-white px-5 py-2.5 text-sm font-bold text-navy-deep hover:-translate-y-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
              <Globe2 className="h-4 w-4" /> Email contact@nyclegal.co.th
            </a>
          </div>
        </section>

        <section className="mt-12 rounded-2xl border border-navy-deep/10 bg-navy-deep/5 p-5">
          <h2 className="font-display text-xl font-extrabold text-navy-deep sm:text-2xl">
            <Landmark className="inline h-5 w-5 text-amber-600" /> Legal framework
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">
            Thai notarial services are governed by the Lawyers Council of Thailand Regulation on Notarial Services Attorneys B.E. 2551 (2008), issued under the Lawyers Act B.E. 2528 (1985). Only attorneys who have completed the prescribed Notarial Services course and registered with the Lawyers Council may perform notarial acts. The Hague Apostille Convention entered into force for Thailand on 13 December 2023; from that date, documents notarised in Thailand and apostilled by the Ministry of Foreign Affairs are accepted in all 124 Hague-member states without further consular legalisation. Notarial Certificates issued by NYC display the attorney's registration number, official seal, bilingual wording, and full address — meeting the Ministry's format requirements for downstream Apostille processing without rework.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Common rejection causes (and how we prevent them)</h2>
          <ul className="mt-5 space-y-3">
            {[
              { m: "Name spelling differs between Thai ID and English passport", p: "Our pre-check cross-references all name fields and flags discrepancies before signing. Where needed we issue an Affidavit of One and the Same Person at the same sitting." },
              { m: "Supporting copies are older than 6 months", p: "We refresh certified copies (passport, house registration, marriage certificate) at the signing appointment so the embassy receives current documents." },
              { m: "Translation quality fails embassy review", p: "Translations are reviewed by our in-house NAATI-certified team before notarisation; we re-translate at no extra cost if required." },
              { m: "Notary certificate wording does not match MFA Apostille format", p: "Our bilingual template is pre-aligned with the MFA's current 2024 Apostille issuance standard — zero rework on Apostille submission." },
            ].map((x, i) => (
              <li key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
                <div className="text-[11px] font-bold uppercase tracking-widest text-rose-700">Rejection cause</div>
                <p className="mt-1 text-sm font-semibold text-slate-900">{x.m}</p>
                <div className="mt-2 text-[11px] font-bold uppercase tracking-widest text-emerald-700">Prevention</div>
                <p className="mt-1 text-sm text-slate-700">{x.p}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Client testimonial</div>
          <blockquote className="mt-3 font-display text-lg leading-relaxed text-navy-deep sm:text-xl">
            "I needed a {n.nameEn} for a transaction at home and was dreading the bureaucracy. NYC's English-speaking team in {loc.nameEn} pre-checked everything by LINE, the attorney came to my condo, and the apostilled document was at my notary in my home country the following week. Genuinely best legal service I've used in Thailand."
          </blockquote>
          <div className="mt-3 text-sm font-semibold text-slate-700">— Senior expat client, {loc.nameEn}</div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Frequently asked questions — {n.nameEn} in {loc.nameEn}</h2>
          <div className="mt-5 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {faqs.map((f, i) => (
              <details key={i} className="group p-4 sm:p-5">
                <summary className="cursor-pointer list-none font-display text-sm font-extrabold text-navy-deep marker:hidden sm:text-base">
                  <span className="mr-2 text-amber-600">Q.</span>{f.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {otherTopActs.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other notarial services in {loc.nameEn}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherTopActs.map((a) => (
                <Link key={a.slug} to="/en/notary/$act/in/$location" params={{ act: a.slug, location: loc.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{a.nameTh}</div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{a.nameEn} · {loc.nameEn}</div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">From ฿{a.priceFromTh.toLocaleString()} · {a.turnaround}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {nearby.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{n.nameEn} · nearby areas</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {nearby.map((l) => (
                <Link key={l.slug} to="/en/notary/$act/in/$location" params={{ act: n.slug, location: l.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{regionEn(l.region)}</div>
                  <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{n.nameEn} · {l.nameEn}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Related notarial acts</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/en/notary/$act" params={{ act: r.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{r.nameTh}</div>
                  <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{r.nameEn}</div>
                  <div className="mt-1 text-[11px] text-slate-500">From ฿{r.priceFromTh.toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14 rounded-2xl border border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-700">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-700">
            <Globe2 className="h-3.5 w-3.5" /> Languages supported
          </div>
          <p className="mt-2 leading-relaxed">
            Service is delivered by default in <strong>English</strong>. On request we also support <strong>Thai-English</strong>, <strong>Japanese</strong>, and <strong>Mandarin Chinese</strong> for client briefings, and our in-house translation team produces certified translations in 24 language pairs including all EU languages plus Arabic, Korean, Vietnamese, Indonesian, and Burmese. For NAATI-certified Thai↔English translations used in Australian immigration, please see our dedicated NAATI service.
          </p>
          <p className="mt-2 leading-relaxed">
            Prefer Thai? Visit the Thai version of this page: <Link to="/notary/$act/in/$location" params={{ act: n.slug, location: loc.slug }} className="text-amber-700 underline hover:text-amber-800">{n.nameTh} ที่{loc.nameTh}</Link>
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Building2 className="inline h-6 w-6 text-amber-600" /> Bundled services for international clients
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            Most international clients in {loc.nameEn} need more than just one notarial act. Common bundles include: <strong>Notary + Certified Translation + Apostille</strong> (one-stop for using Thai documents abroad), <strong>Notary + NAATI translation + visa filing</strong> (for Australian skilled migration), and <strong>Corporate Notary + Apostille + Embassy legalisation</strong> (for offshore company filings). Bundle pricing saves 15-25% versus à la carte and the workflow is managed by one coordinator end-to-end so you have a single point of contact.
          </p>
        </section>

        <TrustLayer />
      </div>
      <EntityPanel slug={loc.slug} locationName={loc.nameEn} locale="en" variant="light" />
      <RelatedServicesInLocation currentService="notary" locationSlug={locSlug} locale="en" />
      <RelatedComparisons category="notary" locale="en" />
      <NotaryActDeepContent act={n} lang="en" />
    </main>
  );
}
